import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ItemSalidaLocal } from '@ausentia/shared';
import { useRed } from '../componentes/useRed.js';
import { fijarOnline } from '../almacen/red.js';
import { descartarItem, itemsOutbox, sincronizar, suscribirOutbox } from '../almacen/outbox.js';

const ESTADO: Record<ItemSalidaLocal['estado'], { txt: string; clase: string }> = {
  ESPERANDO_RED: { txt: 'Esperando red', clase: 'ambar' },
  SUBIENDO: { txt: 'Subiendo…', clase: 'azul' },
  SINCRONIZADO: { txt: 'Sincronizado', clase: 'verde' },
  ERROR: { txt: 'Error', clase: 'roja' },
};

export function Pendientes() {
  const online = useRed();
  const navegar = useNavigate();
  const loc = useLocation();
  const aviso = (loc.state as { aviso?: string } | null)?.aviso;
  const [items, setItems] = useState<ItemSalidaLocal[]>([]);
  const [sincronizando, setSincronizando] = useState(false);

  const refrescar = useCallback(() => {
    void itemsOutbox().then(setItems);
  }, []);

  useEffect(() => {
    refrescar();
    return suscribirOutbox(refrescar);
  }, [refrescar]);

  async function reintentar() {
    setSincronizando(true);
    if (!online) fijarOnline(true); // simula recuperar la señal
    await sincronizar();
    setSincronizando(false);
    refrescar();
  }

  const sinSincronizar = items.filter((i) => i.estado !== 'SINCRONIZADO').length;

  return (
    <div className="contenido">
      <div className="fila" style={{ paddingTop: 6 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>Pendientes de sincronizar · {sinSincronizar}</p>
        <span className="mini" style={{ color: online ? 'var(--verde)' : 'var(--ambar-texto)' }}>
          ● {online ? 'En línea' : 'Sin conexión'}
        </span>
      </div>

      {aviso && <div className="error-box" style={{ background: 'var(--ambar-bg)', color: 'var(--ambar-texto)' }}>{aviso}</div>}

      {items.length === 0 && <p className="muted" style={{ marginTop: 12 }}>No hay nada en la cola. ✅</p>}

      {items.map((i) => {
        const est = ESTADO[i.estado];
        const necesitaFirma = i.estado === 'SINCRONIZADO' && i.challengeId;
        const necesitaActa = i.estado === 'SINCRONIZADO' && i.contingencia && i.constanciaCodigo && !i.challengeId;
        return (
          <div key={i.id} className="tarjeta">
            <div className="fila">
              <div style={{ display: 'flex', gap: 12 }}>
                {i.contingencia && (
                  <span style={{ width: 46, height: 46, borderRadius: 10, background: 'var(--panel-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                    {i.tipo === 'CONTROL_PAPEL' ? '📄' : '📷'}
                  </span>
                )}
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{i.resumen}</p>
                  <p className="muted" style={{ margin: '3px 0 0' }}>
                    {new Date(i.creadoEn).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    {i.constanciaCodigo ? ` · ${i.constanciaCodigo}` : ''}
                  </p>
                  {i.contingencia && (
                    <p className="mini" style={{ color: 'var(--verde-texto)', margin: '5px 0 0' }}>
                      🔒 Hash registrado &nbsp; 📍 GPS &nbsp; 🚫 Fuera de la galería
                    </p>
                  )}
                  {i.error && <p className="mini" style={{ color: 'var(--rojo)', margin: '5px 0 0' }}>{i.error}</p>}
                </div>
              </div>
              <span className={`insignia ${est.clase}`}>{est.txt}</span>
            </div>
            {(necesitaFirma || necesitaActa) && (
              <div className="row-acciones">
                {necesitaFirma && (
                  <button
                    className="primario"
                    onClick={() => navegar('/medico/firma', { state: { codigo: i.constanciaCodigo, challengeId: i.challengeId } })}
                  >
                    Firmar constancia
                  </button>
                )}
                {necesitaActa && (
                  <button className="primario" onClick={() => navegar('/medico/acta')}>
                    Firmar acta de contingencia
                  </button>
                )}
              </div>
            )}
            {i.estado === 'ERROR' && (
              <div className="row-acciones">
                <button className="fantasma" onClick={() => i.id && descartarItem(i.id)}>Descartar</button>
              </div>
            )}
          </div>
        );
      })}

      <div className="aviso-info" style={{ marginTop: 8 }}>
        <span>ℹ️</span>
        <span>Al recuperar señal, la app sube todo sola y te pide firmar el acta de contingencia que acredita la falta de conectividad.</span>
      </div>

      <button className="primario" style={{ marginTop: 10 }} onClick={reintentar} disabled={sincronizando}>
        {sincronizando ? 'Sincronizando…' : '↻ Reintentar conexión'}
      </button>
    </div>
  );
}
